"use client";

// Tremor Raw Drawer [v0.0.1]
import * as DrawerPrimitives from "@radix-ui/react-dialog";

import * as React from "react";
import { RiCloseLine } from "react-icons/ri";

import { ScrollShadow } from "@/components/ui/scroll-shadow";

import { cx, focusRing } from "@/lib/tailwind-utils";

import Button from "./button";

const DrawerPortalContainerContext = React.createContext<HTMLElement | null>(
  null,
);

const DrawerNonModalContext = React.createContext(false);

const Drawer = (
  props: React.ComponentPropsWithoutRef<typeof DrawerPrimitives.Root>,
) => {
  const portalContainer = React.useContext(DrawerPortalContainerContext);

  // When portaled into a scoped container, use non-modal mode so Radix
  // doesn't set body pointer-events:none or trap focus — keeping the
  // right sidebar (and other UI outside the container) interactive.
  const modal = props.modal ?? (portalContainer ? false : undefined);
  return (
    <DrawerNonModalContext.Provider value={modal === false}>
      <DrawerPrimitives.Root tremor-id="tremor-raw" {...props} modal={modal} />
    </DrawerNonModalContext.Provider>
  );
};
Drawer.displayName = "Drawer";

const DrawerTrigger = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitives.Trigger>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitives.Trigger>
>(({ className, ...props }, ref) => {
  return (
    <DrawerPrimitives.Trigger ref={ref} className={cx(className)} {...props} />
  );
});
DrawerTrigger.displayName = "Drawer.Trigger";

const DrawerClose = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitives.Close>
>(({ className, ...props }, ref) => {
  return (
    <DrawerPrimitives.Close ref={ref} className={cx(className)} {...props} />
  );
});
DrawerClose.displayName = "Drawer.Close";

const DrawerPortal = DrawerPrimitives.Portal;

DrawerPortal.displayName = "DrawerPortal";

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitives.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitives.Overlay>
>(({ className, children, style, ...props }, forwardedRef) => {
  return (
    <DrawerPrimitives.Overlay
      ref={forwardedRef}
      className={cx(
        // base
        "fixed inset-0 z-50 overflow-y-auto",
        "data-[state=open]:animate-dialogOverlayShow data-[state=closed]:animate-hide",
        className,
      )}
      style={style}
      {...props}
    >
      {children}
      <div
        className={cx(
          "absolute inset-0 -z-10 bg-background/20 backdrop-blur-sm",
        )}
      ></div>
    </DrawerPrimitives.Overlay>
  );
});

DrawerOverlay.displayName = "DrawerOverlay";

const DrawerContent = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitives.Content> & {
    side?: "left" | "right";
    /** Classes for the full-screen layer the drawer sits in, e.g. its z-index. */
    layerClassName?: string;
  }
>(({ className, children, side = "right", style, ...props }, forwardedRef) => {
  const { layerClassName, ...contentProps } = props;
  const portalContainer = React.useContext(DrawerPortalContainerContext);
  const isNonModal = React.useContext(DrawerNonModalContext);

  const contentEl = (
    <DrawerPrimitives.Content
      ref={forwardedRef}
      className={cx(
        // base
        "absolute inset-y-2 mx-auto flex w-[95vw] flex-1 flex-col border border-border shadow-lg focus:outline-none md:max-w-lg md:rounded-2xl",
        side === "left"
          ? "left-0 rounded-e-2xl data-[state=open]:animate-drawer-slide-in-from-left data-[state=closed]:animate-drawer-slide-out-to-left md:left-2"
          : "right-0 rounded-s-2xl data-[state=open]:animate-drawer-slide-in-from-right data-[state=closed]:animate-drawer-slide-out-to-right md:right-2",
        "bg-background/70 backdrop-blur-md",
        "flex min-h-0 flex-1 flex-col p-4",
        focusRing,
        className,
      )}
      style={style}
      {...((portalContainer || isNonModal) && {
        onPointerDownOutside: (e: CustomEvent) => e.preventDefault(),
        onFocusOutside: (e: CustomEvent) => e.preventDefault(),
        onInteractOutside: (e: CustomEvent) => e.preventDefault(),
      })}
      {...contentProps}
    >
      {children}
      <div
        className={cx(
          "pointer-events-none absolute inset-0 shadow-inner md:rounded-2xl",
          side === "left" ? "rounded-e-2xl" : "rounded-s-2xl",
        )}
      />
    </DrawerPrimitives.Content>
  );

  // When non-modal (via portal container or explicit modal={false}),
  // Radix Overlay returns null, so we render a plain backdrop div instead.
  if (portalContainer || isNonModal) {
    return (
      <DrawerPortal container={portalContainer ?? undefined}>
        <div
          className={cx(
            "z-50 overflow-y-auto data-[state=open]:animate-dialogOverlayShow data-[state=closed]:animate-hide absolute inset-0",
            layerClassName,
          )}
          data-drawer-portal-root
        >
          {contentEl}
          <DrawerPrimitives.Close asChild>
            <div
              className="absolute inset-0 -z-10 bg-background/20 backdrop-blur-sm"
              onPointerDown={(e) => {
                e.currentTarget.dataset.pointerDown = "true";
              }}
              onClick={(e) => {
                if (e.currentTarget.dataset.pointerDown !== "true") {
                  e.preventDefault();
                }
                e.currentTarget.dataset.pointerDown = "false";
              }}
            />
          </DrawerPrimitives.Close>
        </div>
      </DrawerPortal>
    );
  }

  return (
    <DrawerPortal>
      <DrawerOverlay className={layerClassName}>{contentEl}</DrawerOverlay>
    </DrawerPortal>
  );
});

DrawerContent.displayName = "DrawerContent";

const DrawerHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div"> & {
    showCloseButton?: boolean;
    containerClassName?: string;
  }
>(
  (
    {
      children,
      className,
      containerClassName,
      showCloseButton = true,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cx(
          "flex items-start justify-between gap-x-4 px-0 pb-2",
          containerClassName,
        )}
        {...props}
      >
        <div className={cx("mt-1 flex flex-col gap-y-1", className)}>
          {children}
        </div>
        {showCloseButton && (
          <DrawerPrimitives.Close asChild>
            <Button
              variant="ghost"
              className="z-20 aspect-square p-1 hover:bg-hover"
            >
              <RiCloseLine className="size-6" aria-hidden="true" />
            </Button>
          </DrawerPrimitives.Close>
        )}
      </div>
    );
  },
);

DrawerHeader.displayName = "Drawer.Header";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitives.Title>
>(({ className, ...props }, forwardedRef) => (
  <DrawerPrimitives.Title
    ref={forwardedRef}
    className={cx(
      // base
      "text-h3",
      // text color
      "text-foreground",
      className,
    )}
    {...props}
  />
));

DrawerTitle.displayName = "DrawerTitle";

const DrawerBody = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, children, ...props }, ref) => {
  return (
    <ScrollShadow.Root>
      <ScrollShadow.Viewport
        ref={ref}
        className={cx("py-1", className)}
        {...props}
      >
        {children}
      </ScrollShadow.Viewport>
      <ScrollShadow.Top />
      <ScrollShadow.Bottom />
    </ScrollShadow.Root>
  );
});
DrawerBody.displayName = "Drawer.Body";

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitives.Description>
>(({ className, ...props }, forwardedRef) => {
  return (
    <DrawerPrimitives.Description
      ref={forwardedRef}
      className={cx("text-muted-foreground", className)}
      {...props}
    />
  );
});

DrawerDescription.displayName = "DrawerDescription";

const DrawerFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { elevated?: boolean }
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cx("flex flex-row justify-end space-x-2 pt-2", className)}
      {...props}
    />
  );
});

DrawerFooter.displayName = "DrawerFooter";

export {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerPortalContainerContext,
  DrawerTitle,
  DrawerTrigger,
};
